
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { getAllMeals } from "../redux/actions/meals";

import {
  Loader,
  MealsCard,
  Pagination,
} from "../components/utils";

import {
  Fotter,
  Header,
} from "../components/layout";

import { useLocation } from "react-router-dom";
import { useStateContext } from "../context/ContextProvider";
import { media } from "../server";

import { motion } from "framer-motion";

import {
  FiGrid,
  FiList,
  FiColumns,
} from "react-icons/fi";

import { BiRestaurant } from "react-icons/bi";

function AllMeals() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    isLoading,
    Allmeals,
    pagination,
  } = useSelector((state) => state.Meals);

  const { currentDir } = useStateContext();

  const location = useLocation();
  const { category } = location.state || {};
  const categoryId = category?._id;

  const totalPages = pagination?.totalPages || 0;

  const [page, setPage] = useState(0);
  const [limit] = useState(15);

  // Grid or list
  const [viewMode, setViewMode] = useState("grid");

  // One or two columns on mobile
  const [mobileColumns, setMobileColumns] =
    useState("one");

  const [sortOrder, setSortOrder] = useState("");

  useEffect(() => {
    if (categoryId) {
      dispatch(
        getAllMeals(
          page,
          limit,
          categoryId,
          undefined,
          undefined,
          sortOrder
        )
      );
    }
  }, [
    page,
    limit,
    categoryId,
    sortOrder,
    dispatch,
  ]);

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
    setPage(0);
  };

  const handleOneColumnView = () => {
    setViewMode("grid");
    setMobileColumns("one");
  };

  const handleTwoColumnsView = () => {
    setViewMode("grid");
    setMobileColumns("two");
  };

  const handleGridView = () => {
    setViewMode("grid");
  };

  const handleListView = () => {
    setViewMode("list");
  };

  const containerVariants = {
    hidden: {
      opacity: 0,
    },

    visible: {
      opacity: 1,

      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: {
      y: 40,
      opacity: 0,
    },

    visible: {
      y: 0,
      opacity: 1,

      transition: {
        type: "spring",
        stiffness: 100,
        damping: 14,
      },
    },
  };

  const compactCard =
    viewMode === "grid" &&
    mobileColumns === "two";

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Header
        active={4}
        DropActive={1}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative h-[520px] lg:h-[720px]">
          <img
            src={`${media}${category?.Image}`}
            alt={
              currentDir === "ltr"
                ? category?.titleEn
                : category?.titleAr
            }
            className="w-full h-full object-cover scale-105"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />

          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-color-1/20 blur-3xl rounded-full" />

          <div className="absolute inset-0 flex items-end">
            <div className="container pb-16 lg:pb-24">
              <motion.div
                initial={{
                  opacity: 0,
                  y: 80,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  duration: 0.8,
                }}
                className="max-w-4xl"
              >
                <div
                  className="
                    inline-flex
                    items-center
                    gap-3
                    px-5
                    py-3
                    rounded-full
                    bg-white/10
                    backdrop-blur-2xl
                    border
                    border-white/10
                    mb-6
                  "
                >
                  <BiRestaurant className="text-color-1 text-xl" />

                  <span className="text-white font-kufi">
                    {t("Authentic Yemeni Cuisine")}
                  </span>
                </div>

                <h1
                  className="
                    text-5xl
                    md:text-6xl
                    lg:text-7xl
                    font-kufi
                    text-white
                    leading-tight
                    mb-6
                  "
                >
                  {currentDir === "ltr"
                    ? category?.titleEn
                    : category?.titleAr}
                </h1>

                <p className="body-1 text-white/80 max-w-2xl leading-loose">
                  {currentDir === "ltr"
                    ? category?.textEn
                    : category?.textAr}
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section
        id="meals-section"
        className="
          relative
          py-20
          md:py-28
          overflow-hidden
          bg-back-secondary
          dark:bg-back-darkprimery
        "
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-color-1/10 rounded-full blur-3xl" />

        <div className="absolute bottom-0 left-0 w-96 h-96 bg-color-3/10 rounded-full blur-3xl" />

        <div className="container relative z-10">
          {/* Top Bar */}
          <motion.div
            initial={{
              opacity: 0,
              y: -30,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
            }}
            className="
              mb-14
              rounded-[2rem]
              border
              border-color-1/10
              bg-white/70
              dark:bg-back-darksecondary/80
              backdrop-blur-2xl
              shadow-xl
              p-5
              md:p-6
            "
          >
            <div
              className="
                flex
                flex-col
                xl:flex-row
                gap-6
                items-center
                justify-between
              "
            >
              {/* Meals Count and View Controls */}
              <div
                className="
                  w-full
                  xl:w-auto
                  flex
                  flex-col
                  sm:flex-row
                  sm:items-center
                  justify-between
                  xl:justify-start
                  gap-5
                "
              >
                {/* Stats */}
                <div className="flex items-center gap-3">
                  <div
                    className="
                      w-14
                      h-14
                      rounded-2xl
                      bg-color-1/10
                      flex
                      items-center
                      justify-center
                      shrink-0
                    "
                  >
                    <BiRestaurant className="text-color-1 text-2xl" />
                  </div>

                  <div>
                    <h3 className="font-kufi text-tcolor-4 dark:text-tcolor-1">
                      {t("Available Meals")}
                    </h3>

                    <p className="text-gray-500 dark:text-gray-400 text-sm font-kufi">
                      {Allmeals?.length || 0}{" "}
                      {t("items")}
                    </p>
                  </div>
                </div>

                <div className="hidden sm:block w-px h-10 bg-color-1/10" />

                {/* View Buttons */}
                <div
                  className="
                    flex
                    items-center
                    gap-1.5
                    p-1.5
                    rounded-xl
                    bg-back-secondary
                    dark:bg-back-darktertiary
                  "
                >
                  {/* One column on mobile */}
                  <button
                    type="button"
                    onClick={handleOneColumnView}
                    className={`
                      md:hidden
                      w-11
                      h-11
                      rounded-xl
                      flex
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "grid" &&
                        mobileColumns === "one"
                          ? "bg-color-1 text-white shadow-lg"
                          : "text-color-1 hover:bg-color-1/10"
                      }
                    `}
                    aria-label={t("one column view")}
                    title={t("one column view")}
                  >
                    <FiList size={19} />
                  </button>

                  {/* Two columns on mobile */}
                  <button
                    type="button"
                    onClick={handleTwoColumnsView}
                    className={`
                      md:hidden
                      w-11
                      h-11
                      rounded-xl
                      flex
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "grid" &&
                        mobileColumns === "two"
                          ? "bg-color-1 text-white shadow-lg"
                          : "text-color-1 hover:bg-color-1/10"
                      }
                    `}
                    aria-label={t("two columns view")}
                    title={t("two columns view")}
                  >
                    <FiColumns size={19} />
                  </button>

                  {/* Grid on tablet and desktop */}
                  <button
                    type="button"
                    onClick={handleGridView}
                    className={`
                      hidden
                      md:flex
                      w-12
                      h-12
                      rounded-2xl
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "grid"
                          ? "bg-color-1 text-white shadow-xl"
                          : "bg-color-1/10 text-color-1"
                      }
                    `}
                    aria-label={t("grid view")}
                    title={t("grid view")}
                  >
                    <FiGrid size={20} />
                  </button>

                  {/* List */}
                  <button
                    type="button"
                    onClick={handleListView}
                    className={`
                      w-11
                      h-11
                      md:w-12
                      md:h-12
                      rounded-xl
                      md:rounded-2xl
                      flex
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "list"
                          ? "bg-color-1 text-white shadow-xl"
                          : "bg-color-1/10 text-color-1"
                      }
                    `}
                    aria-label={t("list view")}
                    title={t("list view")}
                  >
                    <FiList size={20} />
                  </button>
                </div>
              </div>

              {/* Sort */}
              <div
                className="
                  w-full
                  xl:w-auto
                  flex
                  flex-col
                  sm:flex-row
                  sm:items-center
                  gap-3
                  sm:gap-4
                "
              >
                <span className="font-kufi text-tcolor-4 dark:text-tcolor-1 whitespace-nowrap">
                  {t("sort by")}
                </span>

                <select
                  value={sortOrder}
                  onChange={handleSortChange}
                  className="
                    w-full
                    sm:w-auto
                    bg-white
                    dark:bg-back-darktertiary
                    border
                    border-color-1/10
                    rounded-2xl
                    px-5
                    py-3
                    sm:min-w-[220px]
                    focus:outline-none
                    focus:ring-2
                    focus:ring-color-1
                    font-kufi
                    text-tcolor-4
                    dark:text-white
                  "
                >
                  <option value="">
                    {t("default")}
                  </option>

                  <option value="asc">
                    {t("price low high")}
                  </option>

                  <option value="desc">
                    {t("price high low")}
                  </option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Meals */}
          {isLoading ? (
            <Loader />
          ) : (
            <>
              {Allmeals?.length > 0 ? (
                <motion.div
                  key={`${viewMode}-${mobileColumns}-${sortOrder}`}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className={
                    viewMode === "grid"
                      ? `
                        grid
                        ${
                          mobileColumns === "two"
                            ? "grid-cols-2 gap-3"
                            : "grid-cols-1 gap-6"
                        }
                        md:grid-cols-2
                        md:gap-7
                        xl:grid-cols-3
                        xl:gap-8
                      `
                      : "flex flex-col gap-8"
                  }
                >
                  {Allmeals.map((meal) => (
                    <motion.div
                      key={meal._id}
                      variants={itemVariants}
                      layout
                      className="min-w-0"
                    >
                      <MealsCard
                        meals={meal}
                        category={category}
                        viewMode={viewMode}
                        compact={compactCard}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  className="py-28 text-center"
                >
                  <div
                    className="
                      w-32
                      h-32
                      mx-auto
                      mb-8
                      rounded-full
                      bg-color-1/10
                      flex
                      items-center
                      justify-center
                    "
                  >
                    <BiRestaurant className="text-6xl text-color-1" />
                  </div>

                  <h2 className="text-3xl font-kufi text-tcolor-4 dark:text-tcolor-1 mb-4">
                    {t("no dishes found")}
                  </h2>

                  <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto leading-loose">
                    {t("try another category")}
                  </p>
                </motion.div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <motion.div
                  initial={{
                    opacity: 0,
                    y: 40,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    delay: 0.3,
                  }}
                  className="mt-20 flex justify-center"
                >
                  <div
                    className="
                      bg-white
                      dark:bg-back-darksecondary
                      rounded-[2rem]
                      border
                      border-color-1/10
                      p-3
                      sm:p-5
                      shadow-xl
                    "
                  >
                    <Pagination
                      currentPage={page}
                      totalPages={totalPages}
                      setPage={setPage}
                    />
                  </div>
                </motion.div>
              )}
            </>
          )}
        </div>
      </section>

      <Fotter />
    </>
  );
}

export default AllMeals;

